/**
 * useCounter / CountUp - animate a number from 0 → end when it scrolls into view
 * (once). Respects prefers-reduced-motion (jumps straight to the final value).
 * Pair with mono `tabular-nums` so the width doesn't jitter while counting.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useInView, useReducedMotion } from 'framer-motion';

export function useCounter(end, { duration = 1400 } = {}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const reduce = useReducedMotion();
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!inView || started.current) return;
    started.current = true;
    if (reduce || !Number.isFinite(end)) {
      setValue(end);
      return;
    }
    let raf;
    let t0 = null;
    const step = (ts) => {
      if (t0 == null) t0 = ts;
      const p = Math.min((ts - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(eased * end);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [inView, end, duration, reduce]);

  return [value, ref];
}

// <CountUp value={1234} prefix="₦" /> - formats with thousands separators.
export function CountUp({ value, prefix = '', suffix = '', decimals = 0, format, sx, component = 'span', ...rest }) {
  const [v, ref] = useCounter(value);
  const fmt =
    format ||
    ((n) => `${prefix}${Number(n).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`);
  return (
    <Box ref={ref} component={component} sx={sx} {...rest}>
      {fmt(v)}
    </Box>
  );
}

export default useCounter;
